<template>
  <div class="wd-flex wd-flex-col wd-gap-1 wd-py-4">
    <p class="sheet-title !wd-py-0">{{ $t('product_detail_special_gift') }}</p>
    <a
      class="wd-flex wd-items-center wd-gap-1 wd-py-0.5 wd-text-otempsm wd-text-orange50"
      :href="membershipLink"
      target="_blank"
      rel="noopener noreferrer"
    >
      {{ $t('product_detail_how_to_upgrade') }}
      <FunnowSvgIcon max-width="16" image-path="chevron-right_orange.svg" />
    </a>
  </div>

  <!-- 預訂即送 / 加碼禮 / 生日禮 -->
  <div class="wd-flex wd-flex-col wd-gap-2 xs:wd-gap-3">
    <div v-for="card in rewardUiCards" :key="card.title" class="wd-flex wd-p-4 wd-rounded" :class="card.backgroundColorClass">
      <div class="wd-flex-1" :class="{ 'wd-mr-2': card.isLocked }">
        <p class="!wd-mb-1 wd-text-otempsm" :class="card.foregroundColorClass">{{ card.title }}</p>
        <p class="wd-text-otempsm wd-text-black50" :class="card.type === 'birthday' ? '!wd-mb-1 ' : '!wd-mb-0'" v-html="card.content" />

        <!-- 生日禮：依登入狀態與 is_this_month 顯示 4 種文案 -->
        <template v-if="card.type === 'birthday'">
          <button
            v-if="card.birthdayState === BirthdayCardState.LOGGED_IN_NO_BIRTHDAY"
            class="wd-flex wd-items-center wd-gap-1 wd-py-0.5 wd-text-otempsm wd-text-yellow70 wd-cursor-pointer"
            @click="openBirthdayPicker"
          >
            {{ $t('product_detail_birthday_gift_fill_birthday') }}
            <FunnowSvgIcon max-width="16" image-path="chevron-right.svg" />
          </button>
          <p v-else class="!wd-mb-0 wd-text-otempsm wd-text-gray80">{{ card.hintText }}</p>
        </template>
      </div>
      <!-- 鎖頭圖示：member/vip = 非 PRO/ELITE；birthday = 未登入或無生日 -->
      <FunnowSvgIcon v-if="card.isLocked" class="wd-self-center" :class="card.foregroundColorClass" max-width="20" image-path="lock-icon.svg" />
    </div>
  </div>
  <div v-if="product.benefit" class="wd-flex wd-items-center wd-pt-1 xs:wd-pt-2">
    <FunnowSvgIcon max-width="20" height="20" class="wd-mr-2.5" image-path="fun_coins.svg" />
    <p class="!wd-mb-0 wd-py-2 wd-text-base wd-text-black50">{{ funCoinText }}</p>
  </div>

  <!-- 生日設定 Modal（第一步：日期選擇）-->
  <ui-modal v-model="isBirthdayPickerOpen" :forcing="true" :header-title="$t('birthday_setting_select_hint')" @after-leave="onPickerModalClose">
    <div class="wd-px-2 wd-pb-1">
      <!-- :min/:max 限制 18–100 歲；:month/:year 預設顯示今天 -30 年 -->
      <v-date-picker
        v-model:view-mode="pickerViewMode"
        :model-value="selectedDate"
        color="orange50"
        hide-header
        :min="minBirthdayDate"
        :max="maxBirthdayDate"
        :month="pickerDisplayMonth"
        :year="pickerDisplayYear"
        width="100%"
        @update:model-value="onDateSelected"
      />
    </div>
    <template #buttons>
      <div class="wd-flex wd-px-5 wd-pb-3" :class="isMobile ? '' : 'wd-justify-end'">
        <v-btn
          :block="isMobile"
          class="wd-w-[68px] wd-my-0.5 wd-text-otempmd"
          :height="40"
          color="orange50"
          variant="flat"
          :disabled="!selectedDate"
          @click="onPickerConfirm"
        >
          {{ $t('all_ok') }}
        </v-btn>
      </div>
    </template>
  </ui-modal>

  <!-- 確認儲存變更 Modal（第二步：確認生日） -->
  <ui-modal v-model="isConfirmModalOpen" :forcing="true" :header-title="$t('birthday_setting_confirm_save')">
    <p class="!wd-mb-0 wd-p-5 wd-text-base wd-text-black50">
      {{ $t('birthday_setting_confirm_date_warning', { date: confirmDateFormatted }) }}
    </p>
    <template #buttons>
      <div class="wd-flex wd-px-5 wd-py-3" :class="isMobile ? 'wd-gap-3' : 'wd-gap-2 wd-justify-end'">
        <v-btn :class="isMobile ? 'wd-flex-1' : 'wd-w-[68px]'" :height="40" color="gray20" variant="flat" @click="onConfirmBack">
          {{ $t('all_back') }}
        </v-btn>
        <v-btn
          :class="isMobile ? 'wd-flex-1' : 'wd-w-[68px]'"
          :height="40"
          color="orange50"
          variant="flat"
          :loading="isSubmitting"
          @click="onConfirmSave"
        >
          {{ isMobile ? $t('confirm_change_modal_save_button') : $t('all_ok') }}
        </v-btn>
      </div>
    </template>
  </ui-modal>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { round } from 'lodash-es'

import { BirthdayCardState } from '@/view-models/product'
import type { RewardUiCard, ProductDetailInfo } from '@/view-models/product'
import { MembershipLevel } from '@/view-models/member'
import { FunCoinRewardType } from '@/view-models/order'

import FunnowSvgIcon from '@/components/icons/funnow-svg-icon.vue'
import UiModal from '@/components/dialog/ui-modal.vue'

import { useUserInfo } from '@/composition/useMember'
import { useBreakpoints } from '@/composition/useBreakpoints'
import { useExternalLinks } from '@/composition/useConst'
import { useAlertStore } from '@/store-pinia/alert'

import utils from '@/utils'
import { timeUtil } from '@/utils/time'

interface Props {
  product: ProductDetailInfo
}

type Emits = {
  'birthday-saved': []
}

const { min: minBirthdayDate, max: maxBirthdayDate, defaultMonth: defaultPickerMonth, defaultYear: defaultPickerYear } = getBirthdayPickerConfig()

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const isBirthdayPickerOpen = ref(false)
const isConfirmModalOpen = ref(false)
const selectedDate = ref<Date | null>(null)
const isSubmitting = ref(false)
const pickerViewMode = ref<'month' | 'months' | 'year'>('month')

const { $i18n, $store, $auth } = useNuxtApp()
const { userInfo, isLoggedIn } = useUserInfo()
const { isMobile } = useBreakpoints()
const alertStore = useAlertStore()
const { membershipLink } = useExternalLinks()

// 4 種狀態：依登入 + 是否有生日 + is_this_month 判斷
const birthdayCardState = computed((): BirthdayCardState => {
  if (!isLoggedIn.value) return BirthdayCardState.NOT_LOGGED_IN
  if (!userInfo.value?.birthday) return BirthdayCardState.LOGGED_IN_NO_BIRTHDAY
  if (props.product.birthday_reward_info?.is_this_month) return BirthdayCardState.LOGGED_IN_THIS_MONTH
  return BirthdayCardState.LOGGED_IN_OTHER_MONTH
})

const isBirthdayCardLocked = computed(
  () => birthdayCardState.value === BirthdayCardState.NOT_LOGGED_IN || birthdayCardState.value === BirthdayCardState.LOGGED_IN_NO_BIRTHDAY
)

const birthdayMonthName = computed(() => {
  if (!userInfo.value?.birthday) return ''
  const month = dayjs(userInfo.value.birthday).month() + 1
  const monthMap = $i18n.tm('month') as Record<string, string>
  return monthMap[String(month)] ?? String(month)
})

// 生日禮提示文案（LOGGED_IN_NO_BIRTHDAY 狀態顯示 button，其餘 3 種顯示此文字）
const birthdayHintText = computed((): string => {
  const map: Partial<Record<BirthdayCardState, string>> = {
    [BirthdayCardState.NOT_LOGGED_IN]: $i18n.t('product_detail_birthday_gift_login_hint'),
    [BirthdayCardState.LOGGED_IN_THIS_MONTH]: $i18n.t('product_detail_birthday_gift_eligible_current'),
    [BirthdayCardState.LOGGED_IN_OTHER_MONTH]: $i18n.t('product_detail_birthday_gift_eligible_month', { month: birthdayMonthName.value }),
  }
  return map[birthdayCardState.value] ?? ''
})

const funCoinText = computed((): string => {
  const benefit = props.product.benefit
  if (!benefit) return ''

  const isPercentage = benefit.fun_rewards_type === FunCoinRewardType.Ratio
  const levelName = userInfo.value?.benefit?.level_name
  const percentage = round(100 * (benefit.fun_rewards_per ?? 0), 2)

  if (isLoggedIn.value && levelName) {
    return isPercentage
      ? $i18n.t('product_detail_fun_coin_reward_by_level_percent', { level_name: levelName, fun_rewards: percentage })
      : $i18n.t('product_detail_fun_coin_reward_by_level_fixed', { level_name: levelName, fun_rewards: benefit.fun_rewards_fixed })
  }

  return isPercentage
    ? $i18n.t('product_detail_fun_coin_reward_max_percent', { fun_rewards: percentage })
    : $i18n.t('product_detail_fun_coin_reward_max_fixed', { fun_rewards: benefit.fun_rewards_fixed })
})

// 順序：預訂即送 → 加碼禮 → 生日禮
const rewardUiCards = computed((): RewardUiCard[] => {
  const cards: RewardUiCard[] = []
  const level = userInfo.value?.benefit?.level
  const isElite = level === MembershipLevel.Elite

  if (props.product.member_reward) {
    cards.push({
      type: 'member',
      title: $i18n.t('special_gift_general'),
      content: utils.nl2br(props.product.member_reward),
      backgroundColorClass: 'wd-bg-orange10',
      foregroundColorClass: 'wd-text-orange50',
      isLocked: false,
    })
  }

  if (props.product.vip_reward) {
    cards.push({
      type: 'vip',
      title: $i18n.t('special_gift_pro_elite'),
      content: utils.nl2br(props.product.vip_reward),
      backgroundColorClass: isElite ? 'wd-bg-pink20' : 'wd-bg-brown20',
      foregroundColorClass: isElite ? 'wd-text-pink60' : 'wd-text-brown60',
      isLocked: level !== MembershipLevel.Elite && level !== MembershipLevel.Pro,
    })
  }

  if (props.product.birthday_reward_info?.birthday_reward) {
    cards.push({
      type: 'birthday',
      title: $i18n.t('product_detail_birthday_gift_title'),
      content: utils.nl2br(props.product.birthday_reward_info.birthday_reward),
      backgroundColorClass: 'wd-bg-yellow10',
      foregroundColorClass: 'wd-text-yellow70',
      birthdayState: birthdayCardState.value,
      hintText: birthdayHintText.value,
      isLocked: isBirthdayCardLocked.value,
    })
  }

  return cards
})

// 有已選日期時顯示該日期的月份，否則預設今天 -30 年
const pickerDisplayMonth = computed(() => (selectedDate.value ? selectedDate.value.getMonth() : defaultPickerMonth))
const pickerDisplayYear = computed(() => (selectedDate.value ? selectedDate.value.getFullYear() : defaultPickerYear))
// timeUtil.formatDate 預設 format = 'YYYY/MM/DD'，符合確認 modal 格式
const confirmDateFormatted = computed(() => timeUtil.formatDate(selectedDate.value))
const selectedDateForApi = computed(() => timeUtil.formatDate(selectedDate.value, false, 'YYYY-MM-DD'))

function getBirthdayPickerConfig() {
  const today = dayjs()
  return {
    min: today.subtract(100, 'year').format('YYYY-MM-DD'),
    max: today.subtract(18, 'year').format('YYYY-MM-DD'),
    defaultMonth: today.subtract(30, 'year').month(),
    defaultYear: today.subtract(30, 'year').year(),
  }
}

function openBirthdayPicker() {
  selectedDate.value = null
  pickerViewMode.value = 'month'
  isBirthdayPickerOpen.value = true
}

function onPickerModalClose() {
  isBirthdayPickerOpen.value = false
  if (!isConfirmModalOpen.value) {
    selectedDate.value = null
    pickerViewMode.value = 'month'
  }
}

function onDateSelected(date: Date) {
  selectedDate.value = date
}

function onPickerConfirm() {
  isBirthdayPickerOpen.value = false
  isConfirmModalOpen.value = true
}

function onConfirmBack() {
  isConfirmModalOpen.value = false
  isBirthdayPickerOpen.value = true
}

async function onConfirmSave() {
  if (!selectedDate.value) return

  isSubmitting.value = true
  try {
    const res = await $store.dispatch('authentication/updateUserInfo', {
      birthday: selectedDateForApi.value,
    })

    if (res?.code !== 0) {
      alertStore.showAlert({ message: res?.message || $i18n.t('general_system_error'), type: 'error' })
      return
    }

    // 更新成功但 API 不回新 user，手動 patch $auth.user.birthday 讓 useUserInfo() 立即反映
    const updatedUser = { ...$auth.user, birthday: selectedDateForApi.value }
    $auth.setUser(updatedUser)
    $auth.$storage.setUniversal('user', updatedUser)

    isConfirmModalOpen.value = false
    alertStore.showAlert({ message: $i18n.t('birthday_setting_update_success'), type: 'success' })

    // 通知父層重取方案頁 API（生日影響 birthday_reward_info.is_this_month）
    emit('birthday-saved')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
/* 全域 index.scss 把 .v-picker__body 內的 icon btn 隱藏，覆寫讓生日 picker 的年份切換按鈕（▼）顯示回來 */
:deep(.v-picker__body .v-date-picker-controls > .v-btn--icon) {
  display: flex !important;
}
</style>
